import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTheme } from '../contexts/ThemeContext';

const { FiArrowRight, FiCheckCircle, FiLock } = FiIcons;

const NextChapterButton = ({ folder, folderIndex, completedLessons, onNextChapter, courseData }) => {
  const { isDarkMode } = useTheme();

  const getAllLessonsInFolder = () => {
    return folder.subfolders.reduce((acc, subfolder) => {
      return [...acc, ...subfolder.lessons.filter(l => l.type !== 'text').map(lesson => lesson.id)];
    }, []);
  };

  const allLessonsInFolder = getAllLessonsInFolder();
  
  const isCurrentFolderCompleted = allLessonsInFolder.every(lessonId => 
    completedLessons.includes(lessonId)
  );

  const hasNextChapter = folderIndex < courseData.length - 1;
  const nextChapter = hasNextChapter ? courseData[folderIndex + 1] : null;

  if (!hasNextChapter) {
    return (
      <motion.div 
        className={`mt-8 p-6 rounded-custom border-2 border-dashed text-center ${isDarkMode ? 'border-green-600 bg-green-900/20' : 'border-green-500 bg-green-50'}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        <motion.div 
          className="flex items-center justify-center gap-3 mb-3"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          <SafeIcon icon={FiCheckCircle} className="text-3xl text-green-600" />
        </motion.div>
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
          🎉 Complimenti!
        </h3>
        <p className={`transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Hai completato tutto il videocorso "Appointment Setter"!<br />
          Sei ora pronto per applicare tutte le competenze acquisite.
        </p>
      </motion.div>
    );
  }

  const handleNextChapter = () => {
    if (isCurrentFolderCompleted && hasNextChapter) {
      onNextChapter(nextChapter.id);
    }
  };

  return (
    <motion.div 
      className="mt-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.8 }}
    >
      {isCurrentFolderCompleted ? (
        <motion.button
          onClick={handleNextChapter}
          className={` w-full p-6 rounded-custom font-bold text-lg transition-all duration-300 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-custom-lg hover:shadow-custom-xl transform hover:scale-[1.02] flex items-center justify-center gap-3 relative overflow-hidden `}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.98 }}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <span className="relative z-10 flex items-center gap-3">
            <SafeIcon icon={FiCheckCircle} className="text-2xl" />
            Vai al Prossimo Capitolo
            <SafeIcon icon={FiArrowRight} className="text-2xl" />
          </span>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-500"></div>
        </motion.button>
      ) : (
        <motion.div 
          className={` w-full p-6 rounded-custom border-2 border-dashed text-center ${isDarkMode ? 'border-gray-600 bg-gray-800/50' : 'border-gray-300 bg-gray-50'} `}
          initial={{ opacity: 0.7 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <SafeIcon icon={FiLock} className={`text-2xl ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Prossimo Capitolo Bloccato
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Completa tutte le lezioni di questo capitolo per sbloccare:<br />
            <span className="font-semibold">{nextChapter?.title}</span>
          </p>
          
          <div className="mt-4">
            <div className={`text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Lezioni completate: {completedLessons.filter(id => allLessonsInFolder.includes(id)).length} / {allLessonsInFolder.length}
            </div>
            <div className={`mt-2 h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                style={{ width: `${(completedLessons.filter(id => allLessonsInFolder.includes(id)).length / allLessonsInFolder.length) * 100}%` }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default NextChapterButton;